/**
 * Brazilian-Portuguese display formatters for the /merchant Staff area.
 *
 * Shared contract (fixed by TASK-PLAN P2): every masking formatter strips
 * non-digits and, if the resulting digit count does not match the expected
 * length — or the input is null/undefined/empty — returns the em-dash
 * placeholder `'—'` (the same glyph the mockup uses for "no data"). The only
 * exception is {@link initials}, whose empty fallback is `'?'`.
 *
 * `int` / `brl` use `Intl.NumberFormat('pt-BR')` for locale-correct grouping.
 */

/** Em-dash placeholder rendered wherever a value is missing/invalid. */
export const DASH = '—';

/** Keep only ASCII digits. `null`/`undefined` collapse to an empty string. */
const digits = (v?: string | null): string => (v ?? '').replace(/\D/g, '');

const intFmt = new Intl.NumberFormat('pt-BR');
const brlFmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** '12345678901' → '123.456.789-01'; invalid/empty → '—'. */
export function cpf(v?: string | null): string {
  const d = digits(v);
  if (d.length !== 11) return DASH;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

/** '12345678000190' → '12.345.678/0001-90'; invalid/empty → '—'. */
export function cnpj(v?: string | null): string {
  const d = digits(v);
  if (d.length !== 14) return DASH;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

/**
 * '21987654321' → '(21) 98765-4321' (11-digit mobile);
 * '2123456789'  → '(21) 2345-6789'  (10-digit landline);
 * other lengths/empty → '—'.
 */
export function phone(v?: string | null): string {
  const d = digits(v);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6, 10)}`;
  return DASH;
}

/** '21810000' → '21810-000'; invalid/empty → '—'. */
export function cep(v?: string | null): string {
  const d = digits(v);
  if (d.length !== 8) return DASH;
  return `${d.slice(0, 5)}-${d.slice(5, 8)}`;
}

/** 1820 → '1.820' (pt-BR grouping); null/undefined → '—'. */
export function int(v?: number | null): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  return intFmt.format(v);
}

/**
 * 18420 → 'R$ 18.420,00' (pt-BR currency); null/undefined → '—'.
 *
 * NOTE: `Intl` separates the symbol from the digits with a NBSP (U+00A0),
 * not a regular space. The fixed contract (and format.test.ts) keeps that raw
 * output. Screen-agents comparing brl() to a literal must use a NBSP, or
 * compare against brl() itself instead of a hand-typed string.
 */
export function brl(v?: number | null): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  return brlFmt.format(v);
}

/**
 * 'Jorge Galego' → 'JG'; single word → its first letter; empty/blank → '?'.
 * Takes the first letter of the first two whitespace-separated tokens,
 * uppercased.
 */
export function initials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}
